import {
  Alert,
  Box,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { amazonColors } from "../../../config/amazonTheme";
import { paymentService } from "../../../services/paymentService";
import { useAuthStore } from "../../../store/useAuthStore";
import {
  PaymentError,
  PaymentIntentResponse,
  PaymentSuccess,
} from "../../../types/payment";
import { isTokenExpired } from "../../../utils/jwtUtils";

// Load Stripe from environment variable
// Set REACT_APP_STRIPE_PUBLISHABLE_KEY in Vercel environment variables
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

if (!stripePublishableKey) {
  console.error(
    "REACT_APP_STRIPE_PUBLISHABLE_KEY is not set. Stripe payments will not work."
  );
}

const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null;

interface StripePaymentFormProps {
  amount: number;
  orderNumber: string;
  customerEmail: string;
  customerName: string;
  paymentIntent: PaymentIntentResponse | null;
  onPaymentSuccess: (result: PaymentSuccess) => void;
  onPaymentError: (error: PaymentError) => void;
  onRegisterSubmit?: (submitHandler: (() => Promise<void>) | null) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
  mobileSidebar?: React.ReactNode;
}

const PaymentForm: React.FC<StripePaymentFormProps> = ({
  amount,
  orderNumber,
  customerEmail,
  customerName,
  paymentIntent,
  onPaymentSuccess,
  onPaymentError,
  onRegisterSubmit,
  onProcessingChange,
  mobileSidebar,
}) => {
  const { t: myTranslate } = useTranslation();
  const { user, logout } = useAuthStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const emitError = useCallback(
    (message: string, options?: { code?: string; declineCode?: string }) => {
      onPaymentError({
        code: options?.code || "payment_error",
        message,
        decline_code: options?.declineCode,
      });
    },
    [onPaymentError]
  );

  const processPayment = useCallback(async () => {
    if (!stripe || !elements) {
      emitError(myTranslate("payment.systemNotReady"));
      return;
    }

    if (!paymentIntent) {
      emitError(myTranslate("payment.intentNotAvailable"));
      return;
    }

    if (!paymentIntent.clientSecret) {
      emitError(myTranslate("payment.intentMissingData"));
      return;
    }

    // Check authentication before proceeding
    const token = localStorage.getItem("token");
    if (!token) {
      emitError(myTranslate("payment.authRequired"));
      logout();
      return;
    }

    if (isTokenExpired(token)) {
      emitError(myTranslate("payment.sessionExpired"));
      logout();
      return;
    }

    if (!user) {
      emitError(myTranslate("payment.authRequired"));
      logout();
      return;
    }

    setLoading(true);

    try {
      const cardElement = elements.getElement(CardNumberElement);
      if (!cardElement) {
        throw new Error("Card element not found");
      }

      const { error, paymentIntent: confirmedPaymentIntent } =
        await stripe.confirmCardPayment(
          paymentIntent.clientSecret, // Changed from clientSecret to ClientSecret
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: customerName,
                email: customerEmail,
              },
            },
          }
        );

      if (error) {
        emitError(error.message || myTranslate("payment.paymentFailed"), {
          code: error.code || "payment_failed",
          declineCode: error.decline_code,
        });
        return;
      }

      if (confirmedPaymentIntent.status === "succeeded") {
        // Step 2: Verify payment status with your backend (no need to confirm again)
        setConfirmingPayment(true);
        try {
          // Wait a moment for webhook to process
          await new Promise((resolve) => setTimeout(resolve, 1000));

          // Step 3: Check payment status with your backend
          const paymentStatus = await paymentService.getPaymentStatus(
            confirmedPaymentIntent.id
          );

          if (paymentStatus.status === "succeeded") {
            // Step 4: Call success callback
            onPaymentSuccess({
              paymentIntentId: confirmedPaymentIntent.id,
              orderNumber,
              amount,
              currency: "usd",
            });
          } else {
            throw new Error(
              `Payment status is ${paymentStatus.status}, expected succeeded`
            );
          }
        } catch (backendError: unknown) {
          console.error("Backend payment status check failed:", backendError);
          const backendMessage =
            backendError instanceof Error
              ? backendError.message
              : "Backend status check failed";
          emitError(
            `Payment confirmed with Stripe but backend status check failed: ${backendMessage}`,
            { code: "backend_status_check_failed" }
          );
        } finally {
          setConfirmingPayment(false);
        }
      } else {
        emitError(
          `Payment status is ${confirmedPaymentIntent.status}, expected succeeded`,
          { code: "payment_not_succeeded" }
        );
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error ? err.message : myTranslate("payment.paymentFailed");
      emitError(errorMessage, { code: "payment_failed" });
    } finally {
      setLoading(false);
    }
  }, [
    amount,
    customerEmail,
    customerName,
    elements,
    emitError,
    logout,
    onPaymentSuccess,
    orderNumber,
    paymentIntent,
    stripe,
    user,
  ]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await processPayment();
  };

  useEffect(() => {
    onRegisterSubmit?.(processPayment);
    return () => onRegisterSubmit?.(null);
  }, [onRegisterSubmit, processPayment]);

  useEffect(() => {
    onProcessingChange?.(loading || confirmingPayment);
  }, [confirmingPayment, loading, onProcessingChange]);

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              className="notranslate"
              translate="no"
              sx={{ mb: 1, fontWeight: 500 }}
            >
              {myTranslate("payment.creditOrDebitCard")}
            </Typography>
            {/* Split card fields for better mobile layout */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Card Number - First Row */}
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "grey.300",
                  borderRadius: 1,
                  p: 1.5,
                  backgroundColor: "grey.50",
                  "&:focus-within": {
                    borderColor: amazonColors.orange,
                    boxShadow: `0 0 0 2px ${amazonColors.orange}20`,
                  },
                }}
              >
                <CardNumberElement
                  options={{
                    showIcon: true,
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#424770",
                        "::placeholder": { color: "#aab7c4" },
                      },
                      invalid: { color: "#9e2146" },
                    },
                  }}
                />
              </Box>

              {/* Expiry + CVC - Second Row */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  flexDirection: "row",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: 1,
                    p: 1.5,
                    backgroundColor: "grey.50",
                    "&:focus-within": {
                      borderColor: amazonColors.orange,
                      boxShadow: `0 0 0 2px ${amazonColors.orange}20`,
                    },
                  }}
                >
                  <CardExpiryElement
                    options={{
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#424770",
                          "::placeholder": { color: "#aab7c4" },
                        },
                        invalid: { color: "#9e2146" },
                      },
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    border: "1px solid",
                    borderColor: "grey.300",
                    borderRadius: 1,
                    p: 1.5,
                    backgroundColor: "grey.50",
                    "&:focus-within": {
                      borderColor: amazonColors.orange,
                      boxShadow: `0 0 0 2px ${amazonColors.orange}20`,
                    },
                  }}
                >
                  <CardCvcElement
                    options={{
                      style: {
                        base: {
                          fontSize: "16px",
                          color: "#424770",
                          "::placeholder": { color: "#aab7c4" },
                        },
                        invalid: { color: "#9e2146" },
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </form>
      </Paper>

      {isMobile && mobileSidebar && <Box sx={{ mt: 2 }}>{mobileSidebar}</Box>}
    </Box>
  );
};

const StripePaymentForm: React.FC<StripePaymentFormProps> = (props) => {
  if (!stripePublishableKey || !stripePromise) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">
          Stripe payment is not configured. Please contact support.
        </Alert>
      </Paper>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <PaymentForm {...props} />
    </Elements>
  );
};

export default StripePaymentForm;
